import {Component, OnInit, ViewChild, OnDestroy} from '@angular/core'
import {OrderService} from '../../services/order.service'
import {UserService} from '../../services/user.service'
import {ActivatedRoute, Router} from '@angular/router'
import {MediaObserver} from '@angular/flex-layout'
import {CouponData} from 'src/app/interfaces'
import {FormData, Step, TStepId} from './interfaces'
import {
  FEToBEShipping,
  formDataCustomerInfoToAddressBilingData,
  formDataShippingAddressToAddressShippingData,
  getPaymentInformation,
} from 'src/app/functions';
import {MainComponent} from './components/main/main.component'
import { StripeService, PaymentData } from 'src/app/services/stripe.service'
import { Logger } from 'src/app/services/logger.service'
import { LoaderService } from 'src/app/services/loader.service'
import { FBTrack, FBEvents, GATrack, GAGetEvents, _ltTrigger, sendConversionEventByOrder } from 'src/analytics'
import Regions from 'src/app/data/regions'
import { IShipping, TShippingName } from 'src/app/data/shipping/types'
import { getShippings, getShippingByName, getCurrentShippingName } from 'src/app/data/shipping/utils'
import { SHIPPING_PREMIUM_NAME, SHIPPING_REGULAR_NAME } from 'src/app/data/shipping/constants'
import { getTheCheapestPremiumShippingRegion } from 'src/app/data/regions/utils'
import { IRegion } from 'src/app/data/regions/types'
import Subscriber from 'src/app/subscriber'
import { DiscountService } from 'src/app/services/discount.service'
import { take } from 'rxjs/operators'
import { DialogsService } from 'src/app/shared/dialogs/services/dialogs.service'
import { EFormPaymentType } from 'src/app/shared/form-payment/form-payment.enums'
import { checkIsKonbiniPayment } from 'src/app/services/stripe.service/functions'
import { OrderData } from '@functions/interfaces'
import { RoutingService } from 'app/services/routing.service'

const log = new Logger('OrderComponent');
const sub = new Subscriber();

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss']
})
export class OrderComponent implements OnInit, OnDestroy {
  @ViewChild(MainComponent) mainComponent: MainComponent;

  public orderData: OrderData

  private _formData: FormData;
  // public formData: FormData

  get formData(): FormData {
    return this._formData;
  }

  set formData(formData: FormData) {
    const prevFormData = this._formData;

    this._formData = formData;

    if (formData.formCustomerInfo !== prevFormData?.formCustomerInfo) {
      this._onCustomerInfoUpdate(formData.formCustomerInfo);
    }

    if (formData.formShippingMethod !== prevFormData?.formShippingMethod) {
      this._onShippingMethodUpdate(formData.formShippingMethod);
    }

    if (formData.formShippingAddress !== prevFormData?.formShippingAddress) {
      this._onShippingAdreessUpdate(formData.formShippingAddress);
    }
  }

  public disabledShippingNames: TShippingName[] = []

  /** selected shipping */
  public shippings = getShippings();

  private _shipping?: IShipping;

  get shipping() {
    return this._shipping;
  }

  set shippingName(name: TShippingName) {
    this._updateShipping(name);
  }

  private _step: TStepId = 1

  public readonly steps = 3

  set step(step: Step['id']) {
    const { steps } = this;

    // min max check including thankyou step
    if (step > 0 && step <= steps + 1) {
      this._step = step

      // not including thankyou step
      if (step <= steps) {
        GATrack(GAGetEvents.getCheckoutStep(step));
      } else {
        this.loaderService.hide()
        sendConversionEventByOrder(this.orderData)
      }
    }

    document.documentElement.scrollTop = 0;
  }

  get step(): Step['id'] {
    return this._step
  }

  get isFirstStep(): boolean {
    return this.step === 1
  }

  get isFinalStep(): boolean {
    return this.step === this.steps
  }

  get isCompleted(): boolean {
    return this.step > this.steps
  }

  get isBackDisabled(): boolean {
    return false // this.isFirstStep
  }

  public isNextDisabled = false;

  constructor(
    private orderService: OrderService,
    public userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    public media: MediaObserver,
    public stripeService: StripeService,
    public loaderService: LoaderService,
    private _discountService: DiscountService,
    private _dialogsService: DialogsService,
    private _routing: RoutingService,
  ) {
    sub.push(
      // this.stripeService.paymentSucceeded
      //   .subscribe((data: PaymentData) => {
      //     if (data.id === this.orderData.orderId) {
      //       log.info('paymentSucceeded')
      //     }
      //   }),

      this.stripeService.paymentUpdated
        .subscribe((data: PaymentData) => {
          if (data.id === this.orderData.orderId) {
            const payment = data.payment

            if ('error' in payment) {
              log.error('paymentUpdate: error', new Date(), payment)
              // alert(payment.error)
              alert('お支払いにエラーが発生しました。')
              this.router.navigate(['/cart'])
              this.loaderService.hide()
            } else {
              log.info('paymentUpdate', new Date(), payment)
            }
          }
        }),

      this.orderService.update.subscribe((data: { [orderId: string]: OrderData }) => {
        const order = data[this.orderData?.orderId]

        if (order) {
          log.info('order updated', data)
          this.orderData = order

          if (order.error) {
            alert(order.error.message);
            this._routing.goToIndex();
            this.loaderService.hide();
            return;
          }

          if (order.barOrderId) {
            log.info('got barOrderId', new Date())
            this.step = 4
          }
        }
      }),

      this.stripeService.paymentActionHandle.subscribe(() => {
        const payment = this.stripeService.currentOrderIdPayment;

        if (checkIsKonbiniPayment(payment)) {
          this.step = 4;
          this.loaderService.hide();
        }
      }),
    )
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.show();

    const orderId = this.route.snapshot.paramMap.get('orderId');

    this.stripeService.currentOrderId = orderId;

    this.orderData = await this.orderService.getOrder(orderId);

    this.loaderService.hide();

    // check if order is already paid
    if (this.orderData.barOrderId) {
      this.step = 4;
      return;
    }

    const currentShippingName = getCurrentShippingName(this.orderData.books.length);

    this.shippingName = currentShippingName;

    if (currentShippingName === SHIPPING_PREMIUM_NAME) {
      this.disabledShippingNames.push(SHIPPING_REGULAR_NAME);
    }

    // TODO: remove initial step and setup loader
    this.step = 1;

    FBTrack(FBEvents.initiateCheckout);

    log.info('initialized', this.orderData);
  }

  ngOnDestroy(): void {
    sub.unsubscribe();

    this.stripeService.currentOrderId = undefined;
  }

  public onChange(data: FormData) {
    this.formData = data;
    this.isNextDisabled = false;
  }

  private _onCustomerInfoUpdate(
    formCustomerInfo?: FormData['formCustomerInfo'],
  ): void {
    if (!this.shipping) return;

    this._updateShipping(this.shipping.name);
  }

  private _onShippingMethodUpdate(
    formShippingMethod?: FormData['formShippingMethod'],
  ): void {
    if (this.shipping.name !== formShippingMethod.name) {
      this._shipping = formShippingMethod;
    }
  }

  private _onShippingAdreessUpdate(
    formShippingAddress?: FormData['formShippingAddress'],
  ): void {
    if (!this.shipping) return;

    this._updateShipping(this.shipping.name);
  }

  private _getCurrentRegion(): IRegion {
    let region = getTheCheapestPremiumShippingRegion(Regions);

    const { formData } = this;

    if (formData) {
      const {
        formCustomerInfo,
        formShippingAddress,
      } = formData;

      region =
        Regions[formShippingAddress?.addressFirst]
        || Regions[formCustomerInfo?.addressFirst]
        || region;
    }

    return region
  }

  private _updateShipping(
    name: TShippingName,
  ): void {
    const region: IRegion = this._getCurrentRegion();

    // if price is the same there's no need for update
    if (this.shipping?.price === region.fee.premiumShipping) {
      return;
    }

    this._shipping = getShippingByName(name, region);

    this.shippings = getShippings(region);
  }

  public backClick() {
    if (this.step === 1) {
      this.router.navigate(['/cart'])
    } else {
      this.step--
    }

    console.log('backClick', this.step)
  }

  public async nextClick() {
    log.info('nextClick: step', this.step);

    const isValid = this._validateStep();

    if (!isValid) {
      this.isNextDisabled = true;
      return;
    }


    if (this.step === this.steps) {
      const { formPayment } = this.formData;

      const emit = async () => {
        this.loaderService.show();
        await this.updateOrderData();
        await this.pay();
      }

      if (formPayment.type === EFormPaymentType.konbini) {
        const result = await this._dialogsService
          .showKonbiniConfirmation()
          .afterClosed()
          .toPromise();

        log.info(`nextClick: dialog result: ${result}`);

        if (!result) return;
      }

      await emit();
    } else {
      this.step++;
    }
  }

  private async pay() {
    log.info('pay', new Date());

    const { type } = this.formData.formPayment

    try {
      switch (type) {
        case EFormPaymentType.konbini: {
          const {
            firstName,
            lastName,
            email,
            phoneFirst,
            phoneSecond,
            phoneThird,
          } = this.formData.formCustomerInfo;

          await this.stripeService
            .payWithKonbini(
              this.orderData.orderId,
              `${firstName} ${lastName}`,
              email,
              `${phoneFirst}${phoneSecond}${phoneThird}`,
            );

          break;
        }

        case EFormPaymentType.card: {
          const {
            card,
            name,
            pmToken,
          } = this.formData.formPayment;

          await this.stripeService
            .payWithCard(
              this.orderData.orderId,
              card,
              name,
              pmToken
            );

          break;
        }
      }
    } catch (error) {
      log.error(error);

      // alert(
      //   error?.message ?
      //     error.message :
      //     error
      // )

      alert('お支払いにエラーが発生しました。');

      this.loaderService.hide();
    }
  }

  private async updateOrderData() {
    if (this.orderData) {
      const fD = this.formData

      // local replacing
      this.orderData.addressBillingData
        = formDataCustomerInfoToAddressBilingData(fD.formCustomerInfo);

      // check if the tick 'Ship to the address above' is active
      this.orderData.addressShippingData = fD.formShippingAddressActive
        ? this.orderData.addressBillingData
        : formDataShippingAddressToAddressShippingData(fD.formShippingAddress);

      // const fSM = fD.formShippingMethod

      this.orderData.shippingMethod = FEToBEShipping(this.shipping)

      const fP = fD.formPayment;

      const couponData: CouponData = this.stripeService.coupons[this.orderData.orderId]

      const discountCampaign = await this._discountService.activeDiscountCampaignsObservable.pipe(take(1)).toPromise();

      this.orderData.paymentInformation = getPaymentInformation(
        this.orderData.books,
        this.orderData.shippingMethod,
        this.shipping.price,
        fP.type,
        couponData,
        discountCampaign,
      );

      // saving on the BE side
      return this.orderService.updateOrder(this.orderData)
    }
  }

  /**
   * @returns `true` if valid
   * @returns `false` and touches all the inputs to show up errors if not
   */
  private _validateStep(): boolean {
    const { mainComponent, step } = this;

    if (!mainComponent) return false;

    let status = false;

    switch (step) {
      case 1:
        const {
          formCustomerInfoComponent,
          formShippingAddressComponent,
        } = mainComponent;

        if (!formCustomerInfoComponent || !formShippingAddressComponent) {
          // set status
          status = false;

          break;
        };

        const isFormCustomerInfoValid = formCustomerInfoComponent.formGroup.valid;

        const isFormShippingAddressValid = formShippingAddressComponent.active
          ? isFormCustomerInfoValid
          : formShippingAddressComponent.formGroup.valid;

        // set status
        status = isFormCustomerInfoValid && isFormShippingAddressValid;

        // touch if not valid to show errors
        if (!status) {
          formCustomerInfoComponent.formGroup.markAllAsTouched();

          if (formShippingAddressComponent.active === false) {
            formShippingAddressComponent.formGroup.markAllAsTouched();
          }
        }

        break;
      case 2:
        status = true;

        break;
      case 3:
        const {
          formPaymentComponent,
        } = mainComponent;

        if (!formPaymentComponent) {
          // set status
          status = false;

          break;
        }

        const { formGroup } = formPaymentComponent;

        const isFormPaymentValid = formGroup.valid;

        // set status
        status = isFormPaymentValid;

        // touch if not valid to show errors
        if (!status) {
          formGroup.markAllAsTouched();
        }

        break;
    }

    return status;
  }
}
